// src/redux/careersSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  collection, doc, setDoc, getDocs,
  deleteDoc, query, orderBy, Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

/* ─── Types ─── */
export interface Career {
  id:          string;
  title:       string;
  slug:        string;
  department:  string;
  type:        string;        // "Full-time" | "Part-time" | "Contract"
  location:    string;
  company:     string;
  description: string;
  requirements:string;
  salary:      string;
  published:   boolean;
  coverImage:  string;
  mediaUrls:   string[];      // extra images / videos
  createdAt:   any;
  updatedAt:   any;
}

/* ─────────────────────────────────────────
   THUNKS
───────────────────────────────────────── */

/* UPSERT CAREER */
export const upsertCareer = createAsyncThunk(
  "careers/upsertCareer",
  async (payload: Partial<Career> & { title: string; slug: string }) => {
    const id  = payload.id || payload.slug;
    const ref = doc(db, "careers", id);
    const data = {
      title:        payload.title,
      slug:         payload.slug,
      department:   payload.department   || "",
      type:         payload.type         || "Full-time",
      location:     payload.location     || "",
      company:      payload.company      || "4REVA GROUP",
      description:  payload.description  || "",
      requirements: payload.requirements || "",
      salary:       payload.salary       || "",
      published:    payload.published    ?? false,
      coverImage:   payload.coverImage   || "",
      mediaUrls:    payload.mediaUrls    || [],
      updatedAt:    Timestamp.now(),
      createdAt:    Timestamp.now(),
    };
    await setDoc(ref, data, { merge: true });
    return { id, ...data };
  }
);

/* UPSERT CAREER MEDIA (add extra image/video URLs to an existing career) */
export const upsertCareerMedia = createAsyncThunk(
  "careers/upsertCareerMedia",
  async ({ id, mediaUrls }: { id: string; mediaUrls: string[] }) => {
    const ref = doc(db, "careers", id);
    await setDoc(ref, { mediaUrls, updatedAt: Timestamp.now() }, { merge: true });
    return { id, mediaUrls };
  }
);

/* FETCH ALL CAREERS */
export const fetchCareers = createAsyncThunk(
  "careers/fetchCareers",
  async () => {
    const q        = query(collection(db, "careers"), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Career[];
  }
);

/* DELETE CAREER */
export const deleteCareer = createAsyncThunk(
  "careers/deleteCareer",
  async (id: string) => {
    await deleteDoc(doc(db, "careers", id));
    return id;
  }
);

/* ─────────────────────────────────────────
   SLICE
───────────────────────────────────────── */
const careersSlice = createSlice({
  name: "careers",
  initialState: {
    careers: [] as Career[],
    status:  "idle" as "idle" | "loading" | "failed",
    error:   null as string | null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCareers.pending,   state => { state.status = "loading"; })
      .addCase(fetchCareers.fulfilled, (state, action) => {
        state.status  = "idle";
        state.careers = action.payload;
      })
      .addCase(fetchCareers.rejected, (state, action) => {
        state.status = "failed";
        state.error  = action.error.message || "Failed to fetch careers";
      })
      .addCase(upsertCareer.fulfilled, (state, action) => {
        const idx = state.careers.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.careers[idx] = action.payload as Career;
        else          state.careers.unshift(action.payload as Career);
      })
      .addCase(upsertCareerMedia.fulfilled, (state, action) => {
        const idx = state.careers.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.careers[idx].mediaUrls = action.payload.mediaUrls;
      })
      .addCase(deleteCareer.fulfilled, (state, action) => {
        state.careers = state.careers.filter(c => c.id !== action.payload);
      });
  },
});

export default careersSlice.reducer;